import React, { forwardRef } from 'react'

import { AlertCircle, CheckCircle2 } from 'lucide-react'

import { cn } from '@/utils/cn'

export interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  children: React.ReactNode
  spacing?: 'none' | 'sm' | 'md' | 'lg'
}

const FORM_SPACING = {
  none: 'space-y-0',
  sm: 'space-y-3',
  md: 'space-y-4',
  lg: 'space-y-6'
} as const

export type FormSpacing = keyof typeof FORM_SPACING

export const Form = forwardRef<HTMLFormElement, FormProps>(
  ({ children, className, spacing = 'md', ...props }, ref) => {
    return (
      <form
        ref={ref}
        className={cn('w-full', FORM_SPACING[spacing], className)}
        noValidate
        {...props}
      >
        {children}
      </form>
    )
  }
)

Form.displayName = 'Form'

// Form Message Components
export interface FormMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: 'error' | 'success' | 'info' | 'warning'
  children: React.ReactNode
}

export const FormMessage = forwardRef<HTMLDivElement, FormMessageProps>(
  ({ children, className, type = 'error', ...props }, ref) => {
    const variants = {
      error: 'text-red-600 bg-red-50 border-red-200',
      success: 'text-green-600 bg-green-50 border-green-200',
      info: 'text-blue-600 bg-blue-50 border-blue-200',
      warning: 'text-amber-600 bg-amber-50 border-amber-200'
    }

    const icons = {
      error: AlertCircle,
      success: CheckCircle2,
      info: AlertCircle,
      warning: AlertCircle
    }

    const Icon = icons[type]

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-2 p-3 rounded-md border text-sm',
          variants[type],
          className
        )}
        role={type === 'error' ? 'alert' : 'status'}
        aria-live={type === 'error' ? 'assertive' : 'polite'}
        {...props}
      >
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <div>{children}</div>
      </div>
    )
  }
)

FormMessage.displayName = 'FormMessage'

// Form Section Components
export interface FormSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  description?: string
  children: React.ReactNode
}

export const FormSection = forwardRef<HTMLDivElement, FormSectionProps>(
  ({ title, description, children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('space-y-4', className)} {...props}>
        {(title || description) && (
          <div className="space-y-1">
            {title && <h3 className="text-lg font-medium text-gray-900">{title}</h3>}
            {description && <p className="text-sm text-gray-600">{description}</p>}
          </div>
        )}
        <div className="space-y-4">{children}</div>
      </div>
    )
  }
)

FormSection.displayName = 'FormSection'
